import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import type { MeDTO } from '@wd/shared';
import { ApiError, errorText } from '../lib/api';
import { useHotkeys } from '../lib/hotkeys';
import { useLayout } from '../lib/media';
import { useTheme } from '../lib/theme';
import { useMe, useMembers, useProjects } from '../lib/queries';
import { Icon, StatusIcon, type IconName } from '../ui/icons';
import { Avatar, Button, EmptyState, IconButton, Kbds, LogoMark } from '../ui/primitives';
import './shell.css';

interface NavDef {
  to: string;
  icon: IconName;
  label: string;
  mobileLabel?: string;
  count?: number;
  trail?: ReactNode;
  end?: boolean;
}

function navItems(me: MeDTO): NavDef[] {
  const items: NavDef[] = [
    { to: '/inbox', icon: 'inbox', label: '收件箱', count: me.inboxCount || undefined },
    { to: '/notifications', icon: 'bell', label: '通知', count: me.unreadNotifications || undefined },
    { to: '/', icon: 'doc', label: '我的日报', mobileLabel: '日报', end: true },
    { to: '/projects', icon: 'grid', label: '项目' },
    { to: '/radar', icon: 'radar', label: '雷达' },
    ...(me.programCount > 0 || me.isManager ? [{ to: '/programs', icon: 'list' as const, label: '项目集' }] : []),
    { to: '/ask', icon: 'spark', label: '问 AI' },
  ];
  if (me.isManager) {
    items.push({ to: '/dashboard', icon: 'board', label: '看板' });
    items.push({ to: '/team', icon: 'team', label: '团队状态', trail: <Icon name="lock" size={12} stroke={1.4} /> });
    items.push({ to: '/weekly', icon: 'report', label: '周报' });
  }
  return items;
}

function NavItem({ item, rail }: { item: NavDef; rail?: boolean }) {
  return (
    <NavLink to={item.to} end={item.end} className={({ isActive }) => clsx('wd-nav-item', isActive && 'active')} title={rail ? item.label : undefined}>
      {({ isActive }) => (
        <>
          <Icon name={item.icon} color={isActive ? 'var(--text-2)' : 'var(--icon)'} />
          {rail ? (
            item.count ? <span className="badge">{item.count}</span> : null
          ) : (
            <>
              <span className="flex1">{item.label}</span>
              {item.count ? <span className="count">{item.count}</span> : null}
              {item.trail}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function Wordmark() {
  return (
    <>
      <img src="/wattsonic-wordmark.svg" alt="Wattsonic" className="wordmark only-dark" />
      <img src="/wattsonic-wordmark-dark.svg" alt="Wattsonic" className="wordmark only-light" />
    </>
  );
}

function Sidebar({ me, onSearch }: { me: MeDTO; onSearch: () => void }) {
  const navigate = useNavigate();
  return (
    <aside className="wd-sidebar">
      <div className="wd-ws">
        <LogoMark size={18} />
        <Wordmark />
        <Icon name="chevD" size={12} stroke={1.5} />
      </div>
      <button type="button" className="wd-search" onClick={onSearch}>
        <Icon name="search" size={14} stroke={1.4} />
        <span className="flex1">搜索或跳转</span>
        <Kbds keys={['⌘', 'K']} small />
      </button>
      <nav className="wd-nav">
        {navItems(me).map((it) => (
          <NavItem key={it.to} item={it} />
        ))}
      </nav>
      <div className="wd-side-group">
        <span>团队</span>
        <Icon name="chevD" size={10} stroke={1.8} />
      </div>
      <div className="wd-nav">
        {me.teams.map((t) => (
          <NavLink key={t.id} to={`/teams/${t.id}`} className={({ isActive }) => clsx('wd-nav-item', isActive && 'active')}>
            <span className="wd-team-chip">{t.initial}</span>
            <span>{t.name}</span>
          </NavLink>
        ))}
      </div>
      <div className="wd-side-user">
        <Avatar initial={me.initial} seed={me.name} />
        <span className="c2" style={{ fontSize: 13 }}>
          {me.name}
        </span>
        <span className="flex1 t-meta ellipsis">{me.title ?? me.teamName ?? ''}</span>
        <ThemeButton />
        <IconButton name="gear" label="设置" onClick={() => navigate('/settings')} />
      </div>
    </aside>
  );
}

/** 深浅色切换：侧栏底部（设置旁边）和窄屏侧边条底部 */
function ThemeButton({ rail }: { rail?: boolean }) {
  const [theme, toggle] = useTheme();
  const label = theme === 'dark' ? '切换到浅色' : '切换到深色';
  return rail ? (
    <button type="button" className="wd-nav-item" title={label} aria-label={label} onClick={toggle}>
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
    </button>
  ) : (
    <IconButton name={theme === 'dark' ? 'sun' : 'moon'} label={label} onClick={toggle} />
  );
}

function Rail({ me, onSearch }: { me: MeDTO; onSearch: () => void }) {
  return (
    <aside className="wd-rail">
      <LogoMark size={18} style={{ margin: '7px 0 12px' }} />
      <button type="button" className="wd-nav-item" title="搜索或跳转 ⌘K" onClick={onSearch}>
        <Icon name="search" />
      </button>
      {navItems(me).map((it) => (
        <NavItem key={it.to} item={it} rail />
      ))}
      <span className="flex1" />
      <ThemeButton rail />
      <NavLink to="/me" className={({ isActive }) => clsx('wd-nav-item', isActive && 'active')} title="我">
        <Avatar initial={me.initial} seed={me.name} />
      </NavLink>
    </aside>
  );
}

function TabBar({ me }: { me: MeDTO }) {
  const tabs: NavDef[] = [
    { to: '/', icon: 'doc', label: '日报', end: true },
    { to: '/projects', icon: 'grid', label: '项目' },
    { to: '/inbox', icon: 'inbox', label: '收件箱', count: me.inboxCount || undefined },
    { to: '/me', icon: 'user', label: '我', count: me.unreadNotifications || undefined },
  ];
  // 雷达、团队页在手机上归到“项目”标签下
  const { pathname } = useLocation();
  const also = (to: string) =>
    (to === '/projects' && /^\/(radar|teams)(\/|$)/.test(pathname)) || (to === '/me' && /^\/(settings|weekly|team|notifications|dashboard|ask)(\/|$)/.test(pathname));
  return (
    <nav className="wd-tabbar">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => clsx('wd-tab', (isActive || also(t.to)) && 'active')}>
          {({ isActive }) => (
            <>
              <Icon name={t.icon} size={22} stroke={1.2} color={isActive || also(t.to) ? 'var(--text-1)' : 'var(--icon)'} />
              <span>{t.label}</span>
              {t.count ? <span className="badge">{t.count}</span> : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export function AppShell() {
  const { data: me, error, refetch } = useMe();
  const layout = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const [palette, setPalette] = useState(false);

  useEffect(() => {
    if (me && !me.onboarded && location.pathname !== '/onboarding') navigate('/onboarding', { replace: true });
  }, [me, location.pathname, navigate]);

  useHotkeys(
    {
      'mod+k': () => setPalette(true),
      'modal:mod+k': () => setPalette(false),
      'g i': () => navigate('/inbox'),
      'g n': () => navigate('/notifications'),
      'g a': () => navigate('/ask'),
      'g r': () => navigate('/radar'),
      'g d': () => navigate('/'),
      'g s': () => navigate('/settings'),
      'g w': () => me?.isManager && navigate('/weekly'),
      'g t': () => me?.isManager && navigate('/team'),
      'g b': () => me?.isManager && navigate('/dashboard'),
      c: () => navigate('/'),
    },
    layout !== 'mobile',
  );

  if (!me) {
    if (error && !(error instanceof ApiError && error.status === 401)) {
      return (
        <div className="wd-app" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState icon="wifioff" title="连不上服务器" desc={errorText(error)}>
            <Button onClick={() => void refetch()}>重试</Button>
          </EmptyState>
        </div>
      );
    }
    return <div className="wd-app" />;
  }

  if (layout === 'mobile') {
    return (
      <div className="wd-mobile">
        <div className="wd-mobile-body">
          <Outlet />
        </div>
        <TabBar me={me} />
      </div>
    );
  }
  return (
    <div className="wd-app">
      {layout === 'rail' ? <Rail me={me} onSearch={() => setPalette(true)} /> : <Sidebar me={me} onSearch={() => setPalette(true)} />}
      <main className="wd-main">
        <Outlet />
      </main>
      {palette && <CommandPalette me={me} onClose={() => setPalette(false)} />}
    </div>
  );
}

/** 页面顶栏：面包屑 + 右侧工具 */
export function TopBar({ crumbs, children, icon }: { crumbs: ReactNode[]; children?: ReactNode; icon?: IconName }) {
  return (
    <div className="wd-topbar">
      <div className="wd-crumb">
        {icon && <Icon name={icon} size={14} />}
        {crumbs.map((c, i) => (
          <span key={i} className="row" style={{ gap: 8, minWidth: 0 }}>
            {i > 0 && <span className="sep">›</span>}
            <span className={clsx(i === crumbs.length - 1 ? 'cur' : 'prev', 'ellipsis')}>{c}</span>
          </span>
        ))}
      </div>
      <div className="tools">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ⌘K 命令面板（设计稿 10a）
// ---------------------------------------------------------------------------

interface CmdItem {
  group: '项目' | '人员' | '命令';
  label: string;
  meta?: string;
  icon: ReactNode;
  run: () => void;
  keys?: string[];
}

function CommandPalette({ me, onClose }: { me: MeDTO; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: projects = [] } = useProjects();
  const { data: members = [] } = useMembers();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), []);

  const go = (to: string) => () => {
    onClose();
    navigate(to);
  };
  const all = useMemo<CmdItem[]>(() => {
    const cmds: CmdItem[] = [
      { group: '命令', label: '写今天的日报', icon: <Icon name="edit" />, run: go('/'), keys: ['C'] },
      { group: '命令', label: '转到收件箱', icon: <Icon name="inbox" />, run: go('/inbox'), keys: ['G', 'I'] },
      { group: '命令', label: '查看通知', icon: <Icon name="bell" />, run: go('/notifications'), keys: ['G', 'N'] },
      { group: '命令', label: '转到项目雷达', icon: <Icon name="radar" />, run: go('/radar'), keys: ['G', 'R'] },
      { group: '命令', label: '请假 / 出差', icon: <Icon name="plane" />, run: go('/me?leave=1') },
      { group: '命令', label: '新建项目', icon: <Icon name="plus" />, run: go('/projects?new=1') },
      { group: '命令', label: '项目集 · 进度汇报', icon: <Icon name="list" />, run: go('/programs') },
      ...(me.role === 'admin' ? [{ group: '命令' as const, label: '导入项目（CSV）', icon: <Icon name="export" />, run: go('/projects?import=1') }] : []),
    ];
    if (me.isManager) {
      cmds.push({ group: '命令', label: '看板', icon: <Icon name="board" />, run: go('/dashboard'), keys: ['G', 'B'] });
      cmds.push({ group: '命令', label: '团队状态 · 本周建议关注', icon: <Icon name="team" />, run: go('/team'), keys: ['G', 'T'] });
      cmds.push({ group: '命令', label: '周报', icon: <Icon name="report" />, run: go('/weekly'), keys: ['G', 'W'] });
    }
    cmds.push({ group: '命令', label: '设置', icon: <Icon name="gear" />, run: go('/settings'), keys: ['G', 'S'] });
    return [
      ...projects.map((p) => ({
        group: '项目' as const,
        label: `${p.name}`,
        meta: `${p.code} · ${p.ownerName}`,
        icon: <StatusIcon status={p.status} />,
        run: go(`/projects/${p.id}`),
      })),
      ...members.map((m) => ({
        group: '人员' as const,
        label: m.name,
        meta: m.title ?? m.teamName ?? '',
        icon: <Avatar initial={m.initial} seed={m.name} />,
        run: go(`/people/${m.id}`),
      })),
      ...cmds,
    ];
  }, [projects, members, me.isManager]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? all.filter((it) => `${it.label} ${it.meta ?? ''}`.toLowerCase().includes(s)) : all.filter((it) => it.group === '命令').concat(all.filter((it) => it.group === '项目').slice(0, 5));
    // 输入的像一句问题时，可以直接问 AI
    if (q.trim().length >= 2) list.push({ group: '命令', label: `问 AI：${q.trim()}`, icon: <Icon name="spark" />, run: go(`/ask?q=${encodeURIComponent(q.trim())}`) });
    return list.slice(0, 31);
  }, [q, all]);

  useEffect(() => setSel(0), [q]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')) {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')) {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      filtered[sel]?.run();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  let lastGroup = '';
  return createPortal(
    <div className="wd-scrim" style={{ paddingTop: 120 }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wd-cmd" role="dialog" aria-modal="true" aria-label="命令面板">
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="搜索项目、人员、日报，或输入命令…" />
        <div className="list" role="listbox">
          {filtered.length === 0 && <div className="group">没有找到匹配的结果</div>}
          {filtered.map((it, i) => {
            const header = it.group !== lastGroup ? it.group : null;
            lastGroup = it.group;
            return (
              <div key={`${it.group}-${it.label}-${i}`}>
                {header && <div className="group">{header}</div>}
                <button type="button" role="option" aria-selected={i === sel} className={clsx('item', i === sel && 'sel')} onMouseEnter={() => setSel(i)} onClick={it.run}>
                  {it.icon}
                  <span className="flex1 ellipsis">{it.label}</span>
                  {it.meta && <span className="t-meta">{it.meta}</span>}
                  {it.keys && <Kbds keys={it.keys} />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="foot">
          <span className="wd-hint">
            <Kbds keys={['↑', '↓']} />
            <span>选择</span>
          </span>
          <span className="wd-hint">
            <Kbds keys={['↵']} />
            <span>打开</span>
          </span>
          <span className="wd-hint">
            <Kbds keys={['Esc']} />
            <span>关闭</span>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
